import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Alert,
  Empty,
  message,
  Tooltip,
} from "antd";
import { ReloadOutlined, ToolOutlined, WarningOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import { getSelectedProductionTerminology } from "./productionTerminology";
import { getRegularizationPending } from "./api";

const { Text, Paragraph } = Typography;

// Permission reutilisee : regulariser = lancer une production (coherent avec le
// reste du moteur de production).
const REGULARIZE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.create";

const formatQty = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "0";
  }
  return Number(num.toFixed(3)).toLocaleString("fr-FR");
};

const ProductionRegularization = () => {
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const canRegularize = hasPermission(REGULARIZE_PERMISSION);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const loadPending = useCallback(async () => {
    if (!selectedCompany || !selectedWarehouse) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getRegularizationPending({
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(
        error?.error ||
          "Erreur lors du chargement des régularisations en attente"
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedWarehouse]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  // La regularisation passe par le flux de production standard : on pre-remplit
  // l'unite et la quantite (= deficit) afin de valider la consommation des
  // matieres premieres (et voir les manquants) avant de lancer la production.
  const handleRegularize = (item) => {
    if (!isOnline) {
      message.warning("La régularisation nécessite une connexion internet.");
      return;
    }
    const deficit = Number(item.deficit) || 0;
    if (deficit <= 0) {
      message.info(
        `Aucun déficit à régulariser pour cette ${productionTerminology.unitSingular}.`
      );
      return;
    }
    const params = new URLSearchParams({
      production_unit_id: String(item.production_unit_id),
      output_quantity: String(deficit),
      regularization: "1",
    });
    navigate(`/production/process?${params.toString()}`);
  };

  const columns = [
    {
      title: productionTerminology.unitSingularTitle,
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.warehouse_name ? (
            <Text type="secondary" className="text-xs">
              {record.warehouse_name}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Stock actuel",
      dataIndex: "current_stock",
      key: "current_stock",
      align: "right",
      render: (value, record) => (
        <Tag color="red">
          {formatQty(value)}
          {record.unit_short_name ? ` ${record.unit_short_name}` : ""}
        </Tag>
      ),
    },
    {
      title: "À produire (déficit)",
      dataIndex: "deficit",
      key: "deficit",
      align: "right",
      render: (value, record) => (
        <Text strong>
          {formatQty(value)}
          {record.unit_short_name ? ` ${record.unit_short_name}` : ""}
        </Text>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Tooltip
          title={
            !isOnline
              ? "Indisponible hors ligne"
              : !canRegularize
              ? "Permission requise"
              : `Ouvre le calcul de production (consommation des ${productionTerminology.materialPlural}) avant validation`
          }
        >
          <Button
            type="primary"
            size="small"
            icon={<ToolOutlined />}
            disabled={!canRegularize || !isOnline}
            onClick={() => handleRegularize(record)}
          >
            Régulariser
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (!selectedCompany || !selectedWarehouse) {
    return (
      <Card>
        <Alert
          type="info"
          showIcon
          message="Sélectionnez une entreprise et un établissement"
          description={`La régularisation s'effectue par établissement. Veuillez sélectionner un établissement pour afficher les ${productionTerminology.unitPlural} à régulariser.`}
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <ToolOutlined />
          <span>Régularisation de fin de journée</span>
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={loadPending}
          loading={loading}
        >
          Actualiser
        </Button>
      }
    >
      {!isOnline ? (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message="Mode hors ligne"
          description="La régularisation de production est indisponible hors ligne pour garantir la cohérence du stock. Reconnectez-vous pour régulariser."
        />
      ) : null}

      <Paragraph type="secondary">
        Les {productionTerminology.unitPlural} marquées « vendable sans production » qui ont été vendues
        sans stock apparaissent ici avec un stock négatif. « Régulariser » ouvre
        le calcul de production pré-rempli avec la quantité du déficit : vous
        validez la <Text strong>consommation des {productionTerminology.materialPlural}</Text> (et
        voyez les éventuels manquants) avant de lancer la production qui solde le
        stock.
      </Paragraph>

      <Table
        rowKey={(record) =>
          `${record.production_unit_id}-${record.warehouse_id}`
        }
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`Aucune ${productionTerminology.unitSingular} à régulariser`}
            />
          ),
        }}
      />
    </Card>
  );
};

export default ProductionRegularization;
